/**
 * SecureStorageService - Secure token storage for iOS/Android
 *
 * Uses @aparajita/capacitor-secure-storage for encrypted storage:
 * - iOS: System Keychain (hardware-backed encryption)
 * - Android: AES-GCM encryption with Android KeyStore
 * - Web: Falls back to localStorage (development only)
 */

import { Capacitor } from '@capacitor/core';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';

// Storage keys constants
export const SECURE_STORAGE_KEYS = {
  CUSTOM_TOKEN: 'customToken',
  TOTP_SECRET: 'deviceTotpSecret',
  ACCOUNT_NUMBER: 'deviceAccountNumber',
  // Auth V3 business-device pairing: persisted device session metadata (JSON).
  // The device key pair itself lives in IndexedDB (non-extractable), not here.
  BUSINESS_DEVICE_V3: 'businessDeviceV3',
} as const;

// Key prefix to namespace our app's secure storage
const STORAGE_PREFIX = 'pointcraft_';

// Conditional logger - only logs in development mode
const isDev = import.meta.env.DEV;
const storageLogger = {
  log: (...args: unknown[]): void => {
    if (isDev) console.log('[SecureStorage]', ...args);
  },
  warn: (...args: unknown[]): void => {
    if (isDev) console.warn('[SecureStorage]', ...args);
  },
  error: (...args: unknown[]): void => {
    if (isDev) console.error('[SecureStorage]', ...args);
  },
};

class SecureStorageService {
  private initialized = false;

  /**
   * Initialize the secure storage service.
   * Must be called before using any storage methods on native platforms.
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      storageLogger.log('Already initialized');
      return;
    }

    if (Capacitor.isNativePlatform()) {
      try {
        // Set prefix for all storage operations
        await SecureStorage.setKeyPrefix(STORAGE_PREFIX);

        // Disable iCloud sync for security (tokens should stay on device)
        await SecureStorage.setSynchronize(false);

        storageLogger.log('Initialized with prefix:', STORAGE_PREFIX);
        this.initialized = true;
      } catch (error) {
        storageLogger.error('Failed to initialize:', error);
        // Don't throw - allow fallback to localStorage
        this.initialized = true;
      }
    } else {
      storageLogger.log('Web platform - using localStorage fallback');
      this.initialized = true;
    }
  }

  /**
   * Store a value securely.
   * Uses secure storage on native, localStorage on web.
   */
  // eslint-disable-next-line class-methods-use-this
  async set(key: string, value: string): Promise<void> {
    storageLogger.log('Setting key:', key);

    if (Capacitor.isNativePlatform()) {
      try {
        // API: set(key, data, convertDate?, sync?, access?)
        await SecureStorage.set(key, value, false, false);
        storageLogger.log('Stored securely:', key);
        return;
      } catch (error) {
        storageLogger.error('Secure storage set failed, falling back to localStorage:', error);
        // Fall through to localStorage
      }
    }

    // Fallback to localStorage (web or native failure)
    localStorage.setItem(`${STORAGE_PREFIX}${key}`, value);
    storageLogger.log('Stored in localStorage:', key);
  }

  /**
   * Retrieve a value from secure storage.
   * Uses secure storage on native, localStorage on web.
   */
  // eslint-disable-next-line class-methods-use-this
  async get(key: string): Promise<string | null> {
    storageLogger.log('Getting key:', key);

    if (Capacitor.isNativePlatform()) {
      try {
        // API: get(key, convertDate?, sync?) returns DataType | null
        const result = await SecureStorage.get(key, false, false);
        storageLogger.log('Retrieved from secure storage:', key, result ? 'found' : 'not found');
        return result as string | null;
      } catch (error) {
        storageLogger.warn('Secure storage get failed, checking localStorage:', error);
        // Fall through to check localStorage for migration
      }
    }

    // Fallback to localStorage
    const value = localStorage.getItem(`${STORAGE_PREFIX}${key}`);

    // Also check without prefix for backward compatibility (migration)
    if (!value) {
      const legacyValue = localStorage.getItem(key);
      if (legacyValue) {
        storageLogger.log('Found legacy value in localStorage for:', key);
        return legacyValue;
      }
    }

    storageLogger.log('Retrieved from localStorage:', key, value ? 'found' : 'not found');
    return value;
  }

  /**
   * Remove a value from secure storage.
   * Removes from both secure storage and localStorage for complete cleanup.
   */
  // eslint-disable-next-line class-methods-use-this
  async remove(key: string): Promise<void> {
    storageLogger.log('Removing key:', key);

    if (Capacitor.isNativePlatform()) {
      try {
        // API: remove(key, sync?) returns boolean
        await SecureStorage.remove(key, false);
        storageLogger.log('Removed from secure storage:', key);
      } catch (error) {
        storageLogger.warn('Secure storage remove failed:', error);
      }
    }

    // Always also remove from localStorage (cleanup legacy data)
    localStorage.removeItem(`${STORAGE_PREFIX}${key}`);
    localStorage.removeItem(key); // Also remove legacy key without prefix
    storageLogger.log('Removed from localStorage:', key);
  }

  /**
   * Migrate a token from localStorage to secure storage.
   * Called during app initialization to migrate existing users.
   */
  async migrateFromLocalStorage(key: string): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      storageLogger.log('Migration skipped on web');
      return false;
    }

    try {
      // Check if value exists in localStorage (legacy location)
      const legacyValue = localStorage.getItem(key);

      if (legacyValue) {
        storageLogger.log('Migrating from localStorage to secure storage:', key);

        // Store in secure storage
        await this.set(key, legacyValue);

        // Remove from localStorage after successful migration
        localStorage.removeItem(key);

        storageLogger.log('Migration successful:', key);
        return true;
      }

      storageLogger.log('No legacy value to migrate:', key);
      return false;
    } catch (error) {
      storageLogger.error('Migration failed:', key, error);
      // Don't remove from localStorage if migration failed
      return false;
    }
  }

  /**
   * Check if we're on a native platform with secure storage available.
   */
  // eslint-disable-next-line class-methods-use-this
  isSecureStorageAvailable(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Get the storage key prefix used for namespacing.
   */
  // eslint-disable-next-line class-methods-use-this
  getKeyPrefix(): string {
    return STORAGE_PREFIX;
  }

  /**
   * Get all keys stored in secure storage.
   * Returns keys without the prefix.
   */
  // eslint-disable-next-line class-methods-use-this
  async getAllKeys(): Promise<string[]> {
    storageLogger.log('Getting all keys');

    if (Capacitor.isNativePlatform()) {
      try {
        const keys = await SecureStorage.keys(false);
        storageLogger.log('Retrieved keys from secure storage:', keys.length);
        return keys;
      } catch (error) {
        storageLogger.error('Failed to get keys from secure storage:', error);
        return [];
      }
    }

    // Web fallback: scan localStorage for prefixed keys
    const keys = Object.keys(localStorage)
      .filter((key) => key.startsWith(STORAGE_PREFIX))
      .map((key) => key.replace(STORAGE_PREFIX, ''));

    storageLogger.log('Retrieved keys from localStorage:', keys.length);
    return keys;
  }
}

// Export singleton instance
export const secureStorageService = new SecureStorageService();
